import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Category, Subcategory } from '../types';
import { useNavigate } from 'react-router-dom';
import { categoriesApi } from '../utils/api';

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategoriesMap, setSubcategoriesMap] = useState<Record<number, Subcategory[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await categoriesApi.getAll();
        if (response.data) {
          setCategories(response.data);
          // Fetch subcategories for each category
          const subcategoriesPromises = response.data.map(async (category) => {
            try {
              const subResponse = await categoriesApi.getSubcategories(category.id);
              if (subResponse.data) {
                setSubcategoriesMap(prev => ({
                  ...prev,
                  [category.id]: subResponse.data
                }));
              }
            } catch (err) {
              console.error(`Error fetching subcategories for category ${category.id}:`, err);
            }
          });
          await Promise.all(subcategoriesPromises);
        }
      } catch (err) {
        setError('Failed to load categories. Please try again later.');
        console.error('Error fetching categories:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryId: number) => {
    navigate(`/shop?category=${categoryId}`);
  };

  const handleSubcategoryClick = (categoryId: number, subcategoryId: number) => {
    navigate(`/shop?category=${categoryId}&subcategory=${subcategoryId}`);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">Loading categories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Browse Categories</h1>
      
      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map(category => (
          <div 
            key={category.id}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{category.name}</h2>
              <p className="text-gray-600 mb-4">{category.description}</p>
              
              {/* Subcategories List */}
              {subcategoriesMap[category.id]?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Subcategories:</h3>
                  <div className="flex flex-wrap gap-2">
                    {subcategoriesMap[category.id].map(subcategory => (
                      <button
                        key={subcategory.id}
                        onClick={() => handleSubcategoryClick(category.id, subcategory.id)}
                        className="text-sm px-3 py-1 bg-pink-50 text-pink-600 rounded-full hover:bg-pink-100 transition-colors"
                      >
                        {subcategory.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => handleCategoryClick(category.id)}
                className="text-pink-600 hover:text-pink-700 font-medium"
              >
                View All Products →
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 